import React, { useCallback, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import { cn } from "@/lib/utils";
import { MenuHeader, SubMenu } from "@/types/menu";
import { useMenus } from "@/hooks/menu/useMenus";
import { useMenuNavigation } from "@/hooks/menu/useMenuNavigation";
import { useAprovacoesResumo } from "@/hooks/use-aprovacoes-resumo";
import { useNavigationHelpers } from "@/lib/navigation-helpers";
import { useIsMobile } from "@/hooks/use-mobile";

import {
  SIDEBAR_SEARCH_MIN_ITEMS,
  SIDEBAR_WIDTH_COLLAPSED,
  SIDEBAR_WIDTH_EXPANDED,
  STORAGE_KEY_COLLAPSED,
  STORAGE_KEY_EXPANDED_MENUS,
} from "./constants";
import { filterMenusByQuery } from "./helpers";
import { usePersistentState } from "./usePersistentState";
import { SidebarBrand } from "./components/SidebarBrand";
import { SidebarSearch } from "./components/SidebarSearch";
import { SidebarNav } from "./components/SidebarNav";
import { SidebarToggle } from "./components/SidebarToggle";

interface SidebarProps {
  isMobile: boolean;
  sidebarOpen: boolean;
  setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const Sidebar: React.FC<SidebarProps> = ({ isMobile, sidebarOpen, setSidebarOpen }) => {
  const location = useLocation();
  const { menus, isLoading, hasError } = useMenus();
  const { counts, refresh } = useAprovacoesResumo();
  const navigation = useNavigationHelpers();
  const { navigateToMenuLeaf } = useMenuNavigation({
    onAprovacaoNavigate: () => setTimeout(() => refresh(), 1000),
  });

  const [collapsed, setCollapsed] = usePersistentState<boolean>(STORAGE_KEY_COLLAPSED, false);
  const [expandedMenus, setExpandedMenus] = usePersistentState<string[]>(
    STORAGE_KEY_EXPANDED_MENUS,
    [],
  );
  const [search, setSearch] = useState("");

  const isCollapsed = collapsed && !isMobile;

  // Conta headers + submenus para decidir se a busca compensa: com poucos
  // itens dá pra bater o olho no rail sem precisar filtrar.
  const totalMenuItems = useMemo(
    () => menus.reduce((count, header) => count + 1 + (header.subMenus?.length ?? 0), 0),
    [menus],
  );
  const showSearch = totalMenuItems >= SIDEBAR_SEARCH_MIN_ITEMS;

  // Tablet / notebook estreito (769–1024px): recolhe por padrão para dar mais
  // espaço ao conteúdo. Mobile (≤768) usa o drawer; desktop largo fica livre.
  const isTablet = useIsMobile(1024) && !isMobile;

  React.useEffect(() => {
    if (!isMobile && (location.pathname === "/" || isTablet)) {
      setCollapsed(true);
    }
  }, [location.pathname, isMobile, isTablet, setCollapsed]);

  // Busca só atua no modo expandido e quando há itens o bastante (no rail
  // recolhido não há campo visível; com poucos itens ela nem é exibida).
  const activeQuery = isCollapsed || !showSearch ? "" : search.trim();
  const { filtered, matchedParentIds } = useMemo(
    () => filterMenusByQuery(menus, activeQuery),
    [menus, activeQuery],
  );
  const effectiveExpanded = activeQuery ? matchedParentIds : expandedMenus;

  const handleToggleMenu = useCallback(
    (id: string) => {
      if (isCollapsed) {
        setCollapsed(false);
        setExpandedMenus((prev) => [...prev, id]);
        return;
      }

      setExpandedMenus((prev) =>
        prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id],
      );
    },
    [isCollapsed, setCollapsed, setExpandedMenus],
  );

  const handleNavigate = useCallback(
    (item: MenuHeader | SubMenu) => {
      navigateToMenuLeaf(item);
      if (isMobile) setSidebarOpen(false);
    },
    [navigateToMenuLeaf, isMobile, setSidebarOpen],
  );

  const toggleCollapse = () => {
    if (isMobile) setSidebarOpen((prev) => !prev);
    else setCollapsed((prev) => !prev);
  };

  return (
    <aside
      className={cn(
        "z-50 flex h-screen flex-col shadow-xl",
        "border-r border-sidebar-border bg-sidebar text-sidebar-foreground",
        isMobile
          ? cn(
              "fixed left-0 top-0 transition-transform duration-200 ease-out",
              SIDEBAR_WIDTH_EXPANDED,
              sidebarOpen ? "translate-x-0" : "-translate-x-full",
            )
          : cn(
              "transition-[width] duration-200 ease-in-out",
              isCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED,
            ),
      )}
    >
      <SidebarBrand isCollapsed={isCollapsed} onNavigateHome={navigation.goToHome} />

      {!isCollapsed && showSearch && <SidebarSearch value={search} onChange={setSearch} />}

      <SidebarNav
        menus={filtered}
        isLoading={isLoading}
        hasError={!!hasError}
        isCollapsed={isCollapsed}
        isSearching={!!activeQuery}
        expandedMenus={effectiveExpanded}
        currentPath={location.pathname}
        currentSearch={location.search}
        counts={counts}
        onToggleMenu={handleToggleMenu}
        onNavigate={handleNavigate}
      />

      <SidebarToggle isMobile={isMobile} isCollapsed={isCollapsed} onToggle={toggleCollapse} />
    </aside>
  );
};

export default Sidebar;
